define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dijit/DialogUnderlay",
	"csi",
	"csi/_base/underscore",
	"csi/action/ActionView",
	"csi/action/AggregateMixin",
	"csi/action/Controller",
	"csi/def",
	"csi/def/Field",
	"csi/heading/_base",
	"csi/locale",
	"csi/middleware/goToDialog/goToDialogService",
	"csi/reflection",
	"csi/service/nodeService",
	"csi/store/_base",
	"csi/store/Names",
	"csi/xml",
], function(declare, lang, DialogUnderlay, csi, _, ActionView, AggregateMixin, Controller, def, Field, csiHeading, locale,
	{goToDialog}, reflection, {resolveNodes}, csiStore, Names, xml) {

	return declare([Controller, AggregateMixin], {
		/** @property {Names} _aggregateNamesStore - data store used to hold aggregate store results */
		_aggregateNamesStore: null,

		/** @property {cnl[]} _previousUrls - current set of URLs in attribute being edited */
		_previousUrls: null,

		/** @property {C8Node[]} _sourcesToCheck - selected nodes that each need to be processed in form */
		_sourcesToCheck: null,

		/** @property {CNL[]} relevantUrlsRemoved - relevant removed urls from the response, could either be the same type of the selection node or the type of aggregate class if specified */
		relevantUrlsRemoved: null,

		/**
		 * @override
		 * If more to do, then disable dialog underlay so screen will not flash when closing/opening dialogs
		 */
		closeDialog() {
			if (this._popupDialog) {
				DialogUnderlay.setDisabled(!!this._sourcesToCheck?.length);
			}

			this.inherited(arguments);
		},

		getActionView: function(/*ViewRef?*/vr) {
			// summary: if ViewRef not specified, then look to associated Heading Renderer
			var h = this.m_action.heading;

			if (h && !vr) {
				var ctx = this.getContext();
				var ren = csiHeading.getHeadingRenderer(ctx, h);
				var a = this._getActionViewParameters(ctx, h, ren);
				if (a) {
					vr = a.m_viewRef;
				}
			}

			return this.inherited(arguments, [vr]);
		},

		/**
		 * Return AggregateClass for action
		 * @return {C8NodeType}
		 */
		getAggregateClass() {
			return this.m_action.aggregateClass || this._aggregateNamesStore?.aggregateStoreResponse?.aggregateClass;
		},

		/**
		 * Return AggregateClassId for action
		 * @return {C8AttributeId}
		 */
		getAggregateClassId() {
			return this.m_action.aggregateClassIds?.[0] || this._aggregateNamesStore?.aggregateStoreResponse?.aggregateClassId;
		},

		/**
		 * @override
		 * Configure form data properties when using an aggregate store
		 */
		getFormData() {
			const {
				_aggregateNamesStore,
				m_action: {bAggregateAdd, bAggregateNew},
				m_cache,
				m_formData,
			} = this;

			if (m_formData) {
				return m_formData;
			}

			// create form data based on aggregate store response;
			// store must already have been queried so that we have response
			if (_aggregateNamesStore) {
				const fd = this.m_formData = Controller.prototype.getFormData.call(this);

				fd.m_bOmitSelections = bAggregateNew;
				fd.m_bSelectOne = this.isAggregateSingleSelection();

				if (!bAggregateAdd) {
					// NOTE: this relies on the results being in the cache, which is done elsewhere;
					// another alternative is for the aggregate store response to include the nodes rather than just results,
					// but this is just a byproduct of how FormData works (with nodes rather than urls).
					// Eventually we can get rid of the need for nodes in FormData once aggregate stores are used everywhere.
					_aggregateNamesStore.aggregateStoreResponse.currentValues?.forEach((result) => {
						const node = m_cache.get(result.$id);
						if (node) {
							fd.addAggregatedNode(node);
						}
					});
				}

				return fd;
			}

			return this.inherited(arguments);
		},

		/**
		 * @override
		 * Return true if still more sources to check
		 */
		hasMoreToProcess() {
			return !!this._sourcesToCheck?.length;
		},

		/**
		 * @override
		 * Use React dialog except for wizards
		 */
		preProcess() {
			const av = this.getActionView(this.m_action.m_viewRef);
			const defView = av?.m_defView;
			const nodeUrl = av?.m_node.$URL;

			if (defView?.m_typeView === "Wizard") {
				// use Dojo dialog for wizard
				this.openDialog(av);
			} else {
				// use React dialog
				this._goToDialog(defView, nodeUrl);
			}
		},

		/**
		 * Start process to open dialog (similar to csi/heading/AggregateController)
		 * @param {View} [defView] - view definition to show in popup dialog
		 * @param {cnl} [nodeUrl] - url of node where view is defined
		 */
		async _goToDialog(defView, nodeUrl) {
			const {_aggregateNamesStore, m_action} = this;
			const node = this.getContext().getContextNode();
			const rca = reflection.getAttribute(node, m_action.aggregateId);
			const namesStore = defView ? null : _aggregateNamesStore;

			if (defView || namesStore) {
				// open dialog with explicitly referenced view, or if none, then construct view(s) automatically from aggregate store
				try {
					await goToDialog(
						this,
						rca,
						false,
						namesStore,
						m_action.dialogCardId,
						defView,
						nodeUrl,
						this.onAggregateStoreTableView.bind(this),
					);
				} catch (err) {
					this._onPublishError(err);
				}
			} else {
				// nothing to show (hopefully should never happen)
				console.warn("AggregateFrom action has nothing to display: ", m_action);
				this.postProcess();
			}
		},

		/**
		 * @override
		 * set fixed value for form and set up selected node
		 */
		_onCreatePopup: function(view) {
			if (this._sourcesToCheck) {
				var node = this._sourcesToCheck.shift();

				this.m_cache.cacheNode(node);
				view.cacheView.cacheNode(node);

				this.m_formData.changeSelection(this.widget.getContext(node.$URL), true);

				this.m_action.aggregateClassIds?.forEach((id) => {
					view.addFixedValue(id, node.$URL);
				});

				this.onCreatePopupForm(view, node);
			}

			this.inherited(arguments);
		},

		/**
		 * Extension point to customize the form view based on the selected source node
		 * @param {csi.form.Widget} view
		 * @param {Node} node
		 */
		onCreatePopupForm(view, node) { },

		/**
		 * @override
		 * When using AggregateClass, need to determine actual set of appended and removed nodes
		 */
		onPublishResponse(response) {
			const {
				_aggregateNamesStore,
				_previousUrls,
				m_action: {bOneRequest},
				m_cache,
				m_response,
				urlsAppended,
				urlsRemoved,
			} = this;

			const aggregateClass = this.getAggregateClass();
			const aggregateClassId = this.getAggregateClassId();

			this.relevantUrlsRemoved = [...this.relevantUrlsRemoved || [], ...m_response.getDeletedUrls() || []];

			if (aggregateClass && aggregateClassId && (_aggregateNamesStore || bOneRequest)) {
				const aggregateUrls = this.getAggregateUrls();
				const newUrls = _.difference(aggregateUrls, _previousUrls);
				const oldUrls = _.difference(_previousUrls, aggregateUrls);
				const newNodes = _.compact(m_cache.getNodes(newUrls));

				// only add new nodes that are in cache, as those are the ones user has access to;
				// there could be other nodes in attribute value to which user has no access
				this.urlsAppended = newNodes.map(xml.nodeGetURL);

				// the removed nodes can only be what was deleted in the response and previously in the attribute value
				this.urlsRemoved = _.intersection(oldUrls, this.relevantUrlsRemoved);

				//TODO : Need to add proper fix for this case
				//C8-58240 - Both Console.asser statments fails, When we select Hierarchy ( e.g Question Subsections under Review Template)
				//console.assert(this.urlsAppended.length >= urlsAppended.length, "The number of selections added should equal or be less than number of items added to aggregate attribute");
				//console.assert(this.urlsRemoved.length === urlsRemoved.length, "The number of selections removed should equal number of items deleted from aggregate attribute");
			}
		},

		/**
		 * @override
		 * When controller starts, determine if are using aggregate store, and if so, query for information
		 */
		onStart() {
			// nothing to do here if not yet using aggregate store
			if (!this._useAggregateStore()) {
				return;
			}

			// create names store to query aggregate store; for reverse aggregate cases (not bidirectional), we only want
			// currentValues returned that are part of the result set (query) so that there are no invalid values in dialog
			// (just because we didn't query for them)
			const {
				m_action: {aggregateId, reverseAggregateId, reverseType}
			} = this;

			const node = this._getAggregateRootNode();
			const loc = locale.getLocale();
			const aggregateStoreParams = {
				attributeId: aggregateId,
				locale: loc,
				nodeType: node.$Type,
				reverseAttributeId: reverseAggregateId,
				reverseType,
				sourceUrl: node.$URL,
			};

			this._aggregateNamesStore = new Names({
				aggregateStoreParams,
				loc,
				request: csiStore.getAggregateStoreRequest(aggregateStoreParams),
			});

			// store existing value of attribute if not reverse case
			if (!reverseAggregateId) {
				this._previousUrls = this.getAggregateUrls();
			}
		},

		/**
		 * @override
		 * If using aggregate store, then will need to cache nodes in onPreActionDeferreds rather than here
		 */
		_preActionRequests(requests) {
			if (!this._aggregateNamesStore) {
				this.inherited(arguments);
			}
		},

		/**
		 * @override
		 * If using aggregate store, query for any currently selected nodes
		 */
		onPreActionDeferreds(dfds) {
			const {_aggregateNamesStore, m_cache} = this;

			if (_aggregateNamesStore) {
				dfds.push(new Promise(async (resolve, reject) => {
					try {
						await _aggregateNamesStore.query();

						// NOTE: eventually this will not be necessary as FormData should not require nodes, just urls
						const urls = _aggregateNamesStore.aggregateStoreResponse.currentValues?.map(({$id}) => $id);
						if (urls) {
							await resolveNodes(urls, m_cache);
						}

						resolve();
					} catch(err) {
						reject(err);
					}
				}));
			}
		},

		process: function() {
			// summary: skip call to onAction - instead call once per added/removed node
			// summary: if first time process called, then store selected nodes and determine if they
			// can be published all at once or one-by-one.
			const {m_action, m_formData, _aggregateNamesStore, _sourcesToCheck} = this;

			if (_sourcesToCheck) {
				this._processFormData(m_formData);
			} else {
				const nodes = this._getAggregateSelections(m_formData);

				if (nodes.length && m_action.idForm && this._getFormView(nodes[0], true)) {
					// if form needed for each selection, then need to process additions one by one;
					// but first process any removals
					m_formData.resetSelections(null, true);
					this._processFormData(m_formData);
					this._sourcesToCheck = nodes;
				} else if (_aggregateNamesStore) {
					// call server operation to update aggregate selections
					this._processAggregateStoreSelections(m_formData, m_action.aggregateId, _aggregateNamesStore);

					// force nodes to be returned from action even if action has return parameters so that first request returns any created nodes;
					// we need to do this in order to properly determine the urls of the AggregateClass nodes when that is used
					m_action.requestParams = m_action.requestParams || {};
					m_action.requestParams.ReturnNodes = "All";
				} else {
					this._processFormData(m_formData);
				}
			}

			this.publish();
		},

		toWriterSelections: function(/*FormData*/fd, /*CallMethod?*/call, /*String?*/id) {
			// summary: stub out since Aggregate action handles selected nodes differently
		},

		/**
		 * @override
		 * Process next source only after dialog has been closed for previous one
		 */
		postProcess(bStatus, bRepeat) {
			this.inherited(arguments);

			if ((bStatus !== false) && this._sourcesToCheck?.length) {
				// popup form for next source
				var defView = this._getFormView();

				if (this.m_action.aggregateClassIds) {
					var id = this.m_action.aggregateClassIds[0];
					if (!defView.getField(id)) {
						defView.m_fields.unshift(lang.mixin(new Field(), {
							m_id: id
						}));
					}
				}

				this.m_formData = null;

				this.when(this._popupDialog?._fadeOutDeferred, () => {
					this.resumeProcess();
					this.openDialog(new ActionView(this, defView));
				});
			}
		},

		_getFormView: function(/*Boolean*/bCheckOnly) {
			// summary: get form view for aggregate class
			var rc = reflection.getClass(this.m_action.aggregateClass);
			var defView = rc ? def.getClassViewDef(rc, this.m_action.idForm, false, bCheckOnly) : null;
			if (this.isValidActionView(defView)) {
				return bCheckOnly ? true : defView;
			}

			return null;
		},

		/**
		* @override
		* clear the queue so that we enable the underlay and close the dialog
		*/
		_onCancelPopup: function() {
			if (this.isSuspended()) {
				this._sourcesToCheck = null;
				this.closeDialog();
				this.inherited(arguments);
			}
		},

		/**
		 * @Override
		 * Add the bNewFrom flag here to ensure that super classes are set as New From classes
		 */
		_onCreatePopupParams: function(params) {
			params.bNewFrom = true;
			this.inherited(arguments);
		},

		/**
		 * Determine if aggregate store will be used for action
		 * @return {boolean}
		 */
		_useAggregateStore() {
			const {
				m_action: {
					aggregateClass,
					aggregateClassIds,
					aggregateId,
					bReverse,
					reverseAggregateId,
					m_request,
					m_viewRef
				}
			} = this;

			// assume reverse aggregate stores exist if specified
			if (reverseAggregateId) {
				return true;
			}

			const node = this._getAggregateRootNode();

			// aggregate store does not exist
			if (!reflection.hasAggregateStoreForAttribute(node.$Type, aggregateId)) {
				return false;
			}

			// don't use aggregate store if action explicitly configures properties that would not be necessary with aggregate store
			if (aggregateClass || aggregateClassIds || bReverse || m_request || m_viewRef) {
				return false;
			}

			// don't use aggregate store if this is a wizard
			// NOTE: this will eventually be removed once we start supporting wizards
			if (!!this["onWizardViewCreate"]) {
				return false;
			}

			return true;
		},
	});
});
